/**
 * Reading a menu from a spreadsheet.
 *
 * CSV rather than a real .xlsx: Excel writes and reads CSV natively (File →
 * Save As → CSV), so does Google Sheets, and the browser needs no library to
 * parse it, a real .xlsx parser is a dependency bigger than this whole app.
 *
 * The format is deliberately small: four columns, category first, plus two
 * optional ones for the Tartılı ürünler module (birim: adet/kg, kap: "Porselen
 * tabak 280"). A new
 * category name creates the category; an existing one (matched by name,
 * case- and Turkish-alphabet-insensitively) adds to it. Column order is
 * fixed rather than header-matched, a header row is still expected (and
 * skipped), but its exact wording is never trusted, only its presence.
 */

export interface MenuRow {
  category: string
  name: string
  priceKurus: number
  description: string
  /**
   * 5. sütun "birim": "kg" kiloyla satılır (fiyat 1 kg'ın), "adet" adetle.
   * Boşsa null: var olan ürün olduğu gibi kalır, yeni ürün adetle satılır.
   * Böylece eski 4 sütunlu dosya tartılı ürünü adetliye çevirmez.
   */
  byWeight: boolean | null
  /** 6. sütun "kap": "Porselen tabak 280" (adı ve gramı). Yalnız kg ürününde. */
  tare: { name: string; gr: number } | null
}

export interface MenuCsvError {
  /** 1-indexed as a spreadsheet shows it, header row included. */
  row: number
  message: string
}

export interface ParsedMenuCsv {
  rows: MenuRow[]
  errors: MenuCsvError[]
}

/**
 * One line of CSV, split on commas that are not inside quotes.
 *
 * A hand-rolled splitter rather than a library: the grammar this supports,
 * quoted fields, a doubled quote as an escaped one, is the one thing a
 * spreadsheet export actually produces, and covering the rest of full CSV
 * (embedded newlines, alternate delimiters) would be solving a problem this
 * form never has.
 */
function splitCsvLine(line: string): string[] {
  const fields: string[] = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      fields.push(field)
      field = ''
    } else {
      field += ch
    }
  }
  fields.push(field)
  return fields.map((f) => f.trim())
}

/**
 * A price cell to kuruş. Accepts "300", "300,00", "300.00" and a leading "₺"
 *, the ways a price actually turns up once it has been typed by hand into a
 * spreadsheet by someone who was not thinking about parsers.
 */
function parsePrice(cell: string): number | null {
  const cleaned = cell.replace(/[₺\s]/g, '').replace(',', '.')
  if (!cleaned) return null
  const lira = Number(cleaned)
  if (!Number.isFinite(lira) || lira < 0) return null
  return Math.round(lira * 100)
}

/** "kg", "kilo" → true; "adet" → false; boş → null; başka bir şey → undefined (hata). */
function parseUnit(cell: string): boolean | null | undefined {
  const v = cell.trim().toLocaleLowerCase('tr-TR')
  if (!v) return null
  if (['kg', 'kilo', 'kilogram'].includes(v)) return true
  if (['adet', 'ad', 'tane'].includes(v)) return false
  return undefined
}

/** "Porselen tabak 280", "Porselen tabak 280 g" → {name, gr}; boş → null; gramsız → undefined. */
function parseTare(cell: string): { name: string; gr: number } | null | undefined {
  const v = cell.trim()
  if (!v) return null
  const m = v.match(/^(.*?)\s*(\d+(?:[.,]\d+)?)\s*(?:gr|g|gram)?\.?$/i)
  if (!m || !m[1].trim()) return undefined
  const gr = Math.round(Number(m[2].replace(',', '.')))
  return gr >= 0 && gr <= 20000 ? { name: m[1].trim(), gr } : undefined
}

export function parseMenuCsv(text: string): ParsedMenuCsv {
  // A leading BOM survives Excel's own CSV export and would otherwise land
  // inside the first header cell, making it fail to match anything.
  const body = text.replace(/^﻿/, '')
  const lines = body.split(/\r\n|\r|\n/).filter((l) => l.trim() !== '')

  return parseMenuRows(lines.map(splitCsvLine))
}

/**
 * Satır satır hücrelerden menü: CSV de Excel de buraya geliyor (Excel'de
 * hücreler zaten ayrık, sayı hücresi metne çevrilip veriliyor).
 */
export function parseMenuRows(table: string[][]): ParsedMenuCsv {
  const rows: MenuRow[] = []
  const errors: MenuCsvError[] = []

  table.forEach((rawCells, i) => {
    // Row 1 is the header, whatever it says, skipped on position, not on
    // wording, because "kategori,ürün,fiyat,açıklama" has as many correct
    // spellings as there are people typing it.
    if (i === 0) return

    const cells = rawCells.map((c) => String(c ?? '').trim())
    const [category = '', name = '', priceCell = '', description = '', unitCell = '', tareCell = ''] = cells
    const rowNum = i + 1

    if (!category.trim() && !name.trim() && !priceCell.trim()) return // blank row

    if (!category.trim()) {
      errors.push({ row: rowNum, message: 'Kategori adı boş.' })
      return
    }
    if (!name.trim()) {
      errors.push({ row: rowNum, message: 'Ürün adı boş.' })
      return
    }
    const priceKurus = parsePrice(priceCell)
    if (priceKurus === null) {
      errors.push({ row: rowNum, message: `Fiyat okunamadı: "${priceCell}"` })
      return
    }

    const byWeight = parseUnit(unitCell)
    if (byWeight === undefined) {
      errors.push({ row: rowNum, message: `Birim "adet" ya da "kg" olmalı: "${unitCell}"` })
      return
    }
    const tare = parseTare(tareCell)
    if (tare === undefined) {
      errors.push({ row: rowNum, message: `Kap adı ve gramı okunamadı (örn. "Porselen tabak 280"): "${tareCell}"` })
      return
    }
    if (tare && byWeight !== true) {
      errors.push({ row: rowNum, message: 'Kap yalnız kiloyla satılan (birim "kg") üründe yazılır.' })
      return
    }

    rows.push({
      category: category.trim(),
      name: name.trim(),
      priceKurus,
      description: description.trim(),
      byWeight,
      tare,
    })
  })

  return { rows, errors }
}
